import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import api from "../api/api"; // axios instance

export default function ReviewsSection({ reviews: initialReviews, tourId }) {
  const [reviews, setReviews] = useState(initialReviews);
  const [canReview, setCanReview] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  // Check if user can review
  useEffect(() => {
    async function check() {
      try {
        const res = await api.get(`/tours/${tourId}/can-review`);
        setCanReview(res.data.canReview);
      } catch (err) {
        setCanReview(false);
      }
    }
    check();
  }, [tourId]);

  // Submit review
  const handleSubmit = async () => {
    if (!rating) return alert("Please select rating");

    setLoading(true);
    try {
      const res = await api.post(`/tours/${tourId}/reviews`, {
        rating,
        comment,
      });

      setReviews(res.data.reviews);
      setRating(0);
      setComment("");
    } catch (e) {
      alert(e.response?.data?.message || "Something went wrong");
    }
    setLoading(false);
  };

  const averageRating = (
    reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
  ).toFixed(1);

  return (
    <div className="py-12 space-y-8">
      <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Reviews & Ratings</h2>

      {/* Rating Summary */}
      {reviews.length > 0 && (
        <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-8 border border-orange-200 dark:border-orange-800">
          <div className="flex items-center gap-4">
            <div className="text-5xl font-bold text-orange-600 dark:text-orange-400">
              {averageRating}
            </div>
            <div>
              <div className="flex gap-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={20}
                    className={
                      i < Math.floor(averageRating)
                        ? "fill-orange-500 text-orange-500"
                        : "text-gray-300 dark:text-gray-600"
                    }
                  />
                ))}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Based on {reviews.length} {reviews.length === 1 ? "review" : "reviews"}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Review Form (Only if user booked) */}
      {canReview && (
        <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-gray-200 dark:border-slate-800">
          <h3 className="font-semibold mb-4 text-gray-900 dark:text-white">Write a Review</h3>

          {/* Rating input */}
          <div className="flex gap-2 mb-4">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                size={28}
                onClick={() => setRating(star)}
                className={
                  star <= rating
                    ? "fill-orange-500 text-orange-500 cursor-pointer"
                    : "text-gray-300 dark:text-gray-600 cursor-pointer"
                }
              />
            ))}
          </div>

          {/* Comment */}
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="w-full p-4 border dark:bg-slate-800 border-gray-300 dark:border-slate-700 rounded-lg"
            placeholder="Write your experience..."
            rows="4"
          />

          <button
            disabled={loading}
            onClick={handleSubmit}
            className="mt-4 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-semibold"
          >
            {loading ? "Submitting..." : "Submit Review"}
          </button>
        </div>
      )}

      {/* If user can't review */}
      {!canReview && (
        <p className="text-gray-500 dark:text-gray-400 italic">
          Only customers who booked this tour can leave a review.
        </p>
      )}

      {/* Review List */}
      <div className="space-y-4">
        {reviews.map((review) => (
          <div
            key={review.id}
            className="bg-white dark:bg-slate-900 rounded-lg p-6 border border-gray-200 dark:border-slate-800 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">{review.name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{review.email}</p>
              </div>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={16}
                    className={
                      i < review.rating
                        ? "fill-orange-500 text-orange-500"
                        : "text-gray-300 dark:text-gray-600"
                    }
                  />
                ))}
              </div>
            </div>
            <p className="text-gray-600 dark:text-gray-400">{review.comment}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
